/** @jsx jsx */
import React, { useState, useEffect, useContext } from "react";
import { css, jsx } from "@emotion/react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Container from "@mui/material/Container";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import CircularProgress from "@mui/material/CircularProgress";
import Chip from "@mui/material/Chip";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import Rating from "@mui/material/Rating";
import Paper from "@mui/material/Paper";
import Grid from "@mui/material/Grid";
import { Slide } from "react-slideshow-image";
import "react-slideshow-image/dist/styles.css";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import VerifiedIcon from "@mui/icons-material/Verified";
import { postRecord, handleApiError } from "services/services";
import { AuthContext } from "context/Authentication";

const API_GetAllVendors = "vendors/getall";
const API_GetVendorLocations = "vendor-locations/getbyvendorid";
const API_GetAllLocations = "locations/getall";

// Responsive settings for the slider
const responsiveSettings = [
  { breakpoint: 1900, settings: { slidesToShow: 5, slidesToScroll: 1 } },
  { breakpoint: 1200, settings: { slidesToShow: 4, slidesToScroll: 1 } },
  { breakpoint: 800, settings: { slidesToShow: 3, slidesToScroll: 1 } },
  { breakpoint: 500, settings: { slidesToShow: 2, slidesToScroll: 1 } },
  { breakpoint: 0, settings: { slidesToShow: 1, slidesToScroll: 1 } },
];

export default function TopSuppliers() {
  const context = useContext(AuthContext);
  const navigate = useNavigate();
  const [vendors, setVendors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let isMounted = true;

    const initializeData = async () => {
      try {
        await fetchAllVendors(isMounted);
        await fetchAllLocations(isMounted);
      } catch (error) {
        if (isMounted) {
          console.error("Error initializing data:", error);
        }
      }
    };

    initializeData();

    return () => {
      isMounted = false;
    };
  }, []);

  const fetchAllVendors = async (isMounted) => {
    try {
      const response = await postRecord(API_GetAllVendors, {});
      if (isMounted && response.status === 200 && response.data) {
        console.log("All Vendors:", response.data);
        const sponsoredVendors = response.data.filter(
          (vendor) => vendor.Is_Sponsored === "1"
        );
        console.log("Sponsored Vendors:", sponsoredVendors);
        await fetchVendorLocations(sponsoredVendors, isMounted);
      }
    } catch (error) {
      if (isMounted) {
        console.log(error);
        setLoading(false);
      }
    }
  };

  const fetchAllLocations = async (isMounted) => {
    try {
      const response = await postRecord(API_GetAllLocations, {});
      if (isMounted && response.status === 200 && response.data) {
        setAllLocations(response.data);
      }
    } catch (error) {
      if (isMounted) {
        console.log(error);
      }
    }
  };

  const fetchVendorLocations = async (vendors, isMounted) => {
    try {
      const vendorPromises = vendors.map((vendor) =>
        postRecord(API_GetVendorLocations, { Vendor_Id: vendor.Vendor_Id })
          .then((response) => {
            if (response.status === 200 && response.data) {
              vendor.Service_Locations = response.data.map(
                (location) => location.Location_Id
              );
            }
          })
          .catch((error) => {
            console.log(error);
          })
      );

      await Promise.all(vendorPromises);

      if (isMounted) {
        setVendors(vendors);
        setLoading(false);
      }
    } catch (error) {
      if (isMounted) {
        console.log(error);
        setLoading(false);
      }
    }
  };

  const getLocationName = (locationId) => {
    const location = allLocations.find((loc) => loc.Location_Id === locationId);
    return location ? location.Location_Name : "Unknown Location";
  };

  const renderVendorCard = (vendor) => {
    const rating = (Math.random() * 2 + 3).toFixed(1); // Random rating between 3.0 and 5.0
    const isProductSetting = context?.state?.appSettings?.some(
      (setting) =>
        setting.Setting_Name === "Vendor Offering" &&
        setting.Setting_Value === "Services"
    );

    return (
      <Card
        key={vendor.Vendor_Id}
        sx={{
          mx: 1,
          my: 1,
          transition: "all 0.3s ease-in-out",
          backdropFilter: "blur(10px)",
          borderRadius: "12px",
          cursor: "pointer",
          textAlign: "left",
          position: "relative",
          backgroundColor: "background.paper",
          "&:hover": {
            transform: "scale(1.05)",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <CardMedia
            sx={{ height: 150 }}
            image={process.env.BASEURL + vendor.Vendor_Banner}
            title={vendor.Vendor_Name}
          />

          {/* Display round cover image badge if exists */}
          {vendor.Vendor_Cover_Img && (
            <Avatar
              src={process.env.BASEURL + vendor.Vendor_Cover_Img}
              alt={`${vendor.Vendor_Name} cover`}
              sx={{
                width: 70,
                height: 70,
                position: "absolute",
                top: 110,
                left: 10,
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              }}
            />
          )}

          <Box
            sx={{
              position: "absolute",
              top: 8,
              left: 8,
              display: "none",
              alignItems: "center",
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              borderRadius: "4px",
              padding: "2px 8px",
            }}
          >
            <Rating value={parseFloat(rating)} precision={0.1} readOnly />
            <Typography variant="body2" sx={{ ml: 0.5, color: "white" }}>
              ({rating})
            </Typography>
          </Box>
        </Box>
        <Paper
          sx={{
            width: 100,
            height: 80,
            position: "absolute",
            top: 100,
            right: 2,
          }}
        >
          <img
            src={process.env.BASEURL + vendor.Vendor_Logo}
            alt={vendor.Vendor_Name}
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              objectFit: "contain",
              objectPosition: "center",
            }}
          />
        </Paper>

        <CardContent sx={{ pt: 4 }}>
          <Typography variant="h6" sx={{ fontWeight: "bold" }}>
            {vendor.Vendor_Name}
            {vendor.Is_Sponsored === "1" && (
              <VerifiedIcon sx={{ color: "primary.main", ml: 1 }} />
            )}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1,
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              WebkitLineClamp: 2,
              overflow: "hidden",
            }}
          >
            {vendor.Vendor_Short_Bio}
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 1 }}>
            {vendor.Service_Locations.map((locationId) => (
              <Chip
                key={locationId}
                label={getLocationName(locationId)}
                size="small"
              />
            ))}
          </Box>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            sx={{ mt: 2 }}
            onClick={() => navigate(`/VendorPreview/${vendor.Vendor_Id}`)}
          >
            {isProductSetting ? "View Services" : "View Menu"}
          </Button>
        </CardContent>
      </Card>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4, my: 6 }}>
      <Typography
        variant="h4"
        align="center"
        sx={{ fontWeight: "bold", mb: 2 }}
      >
        Top Suppliers
      </Typography>
      <Box
        sx={{
          width: "80px",
          height: "2px",
          backgroundColor: "primary.main",
          mx: "auto",
          mb: 4,
        }}
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : vendors.length > 0 ? (
        vendors.length > 4 ? (
          <Slide
            responsive={responsiveSettings}
            indicators={true}
            infinite={true}
            autoplay={true}
            autoplayInterval={3000}
          >
            {vendors.map((vendor) => (
              <Box key={vendor.Vendor_Id} sx={{ p: 1 }}>
                {renderVendorCard(vendor)}
              </Box>
            ))}
          </Slide>
        ) : (
          <Grid container spacing={2} justifyContent="center">
            {vendors.map((vendor) => (
              <Grid
                item
                xs={12}
                sm={6}
                md={4}
                lg={3}
                xl={3}
                key={vendor.Vendor_Id}
              >
                {renderVendorCard(vendor)}
              </Grid>
            ))}
          </Grid>
        )
      ) : (
        <Typography variant="body1" align="center">
          No suppliers found
        </Typography>
      )}
    </Container>
  );
}
